import { useState, useEffect, useCallback, useRef } from "react";
import { useStreamingApi } from "@/hooks/useApi";
import { useOrgFromPathname } from "@/hooks/useOrgFromPathname";
import type { JobLogsResponse } from "@/hooks/useJobDetails";
import { useJobLogsMetadata, getDefaultLogsMetadataEntry, getLogsMetadataEntryBySource, downloadAllBlobs } from "@/hooks/useJobLogs";
import { isAbortError } from "@/lib/utils";
export interface StreamingJobLogsState {
  logs: JobLogsResponse;
  isLoading: boolean;
  isComplete: boolean;
  error: Error | null;
  totalRecords: number | null;
  batchCount: number;
}
export type SelectedLogSource = {
  sourceType: string;
  sourceName: string;
} | null;

// How often to poll for new log lines while a job is still running. Mirrors the
// metrics refetch interval in useJobDetails. We deliberately use a coarse cadence
// here because the underlying ClickHouse query can be expensive on busy jobs.
const RUNNING_JOB_LOG_POLL_INTERVAL_MS = 10_000;
export function useStreamingJobLogs(jobId: string, enabled: boolean = true, runnerName?: string | null, runId?: string, selectedLogSource?: SelectedLogSource, isJobRunning: boolean = false) {
  const {
    streamingFetch
  } = useStreamingApi();
  // Get org from pathname directly - available immediately unlike router.query
  const activeGithubOrg = useOrgFromPathname();

  // Check for blob storage metadata from job details
  const {
    data: metadata,
    isLoading: isMetadataLoading,
    error: metadataError
  } = useJobLogsMetadata(activeGithubOrg || "", jobId, runId);
  const [state, setState] = useState<StreamingJobLogsState>({
    logs: [],
    isLoading: enabled,
    isComplete: false,
    error: null,
    totalRecords: null,
    batchCount: 0
  });
  const abortControllerRef = useRef<AbortController | null>(null);
  const blobAbortControllerRef = useRef<AbortController | null>(null);
  const pollAbortControllerRef = useRef<AbortController | null>(null);
  const hasFetchedRef = useRef(false);
  const metadataRef = useRef<typeof metadata>(null);
  // Track the highest log_idx we've already appended so polling can ask the
  // server for only the new tail. log_idx is generated server-side off
  // rowNumberInAllBlocks() and is monotonic for a given (vm_id, record_type)
  // stream, so this is safe even when new rows arrive between polls.
  const lastLogIdxRef = useRef<number>(0);
  useEffect(() => {
    metadataRef.current = metadata ?? null;
  }, [metadata]);
  const fetchFromBlobStorage = useCallback(async (source: SelectedLogSource): Promise<true | false | "aborted"> => {
    const currentMetadata = metadataRef.current;
    const entry = source?.sourceType != null && source?.sourceName != null ? getLogsMetadataEntryBySource(currentMetadata ?? null, source.sourceType, source.sourceName) : getDefaultLogsMetadataEntry(currentMetadata ?? null);
    if (!entry) {
      return false;
    }

    // Cancel any existing blob fetch request
    if (blobAbortControllerRef.current) {
      blobAbortControllerRef.current.abort();
    }
    const controller = new AbortController();
    blobAbortControllerRef.current = controller;
    setState(prev => ({
      ...prev,
      isLoading: true,
      isComplete: false,
      error: null,
      logs: [],
      totalRecords: null,
      batchCount: 0
    }));
    try {
      const logs = await downloadAllBlobs(entry, (loaded, total) => {
        if (controller.signal.aborted) {
          return;
        }
        setState(prev_1 => ({
          ...prev_1,
          batchCount: loaded
        }));
      }, controller.signal);
      if (controller.signal.aborted) {
        return "aborted";
      }
      setState(prev_2 => ({
        ...prev_2,
        logs,
        isLoading: false,
        isComplete: true,
        totalRecords: entry.total_lines
      }));
      return true;
    } catch (error: any) {
      if (controller.signal.aborted || isAbortError(error)) {
        return "aborted";
      }
      console.warn("Failed to download from blob storage, falling back to streaming:", error);
      setState(prev_0 => ({
        ...prev_0,
        isLoading: false,
        isComplete: false,
        error: null
      }));
      return false;
    }
  }, []);
  const fetchStreamingLogs = useCallback(async () => {
    if (!activeGithubOrg || !jobId || !enabled) {
      return;
    }

    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller_0 = new AbortController();
    abortControllerRef.current = controller_0;
    setState(prev_3 => ({
      ...prev_3,
      isLoading: true,
      isComplete: false,
      error: null,
      logs: [],
      totalRecords: null,
      batchCount: 0
    }));
    try {
      let url = `user/github/orgs/${activeGithubOrg}/metrics/logs/job/stream?job_id=${jobId}&limit=1000000`;
      if (runnerName) {
        url += `&vm_id=${encodeURIComponent(runnerName)}`;
      }
      const response = await streamingFetch(url, {
        signal: controller_0.signal
      });
      if (!response.ok) {
        if (response.status === 404) {
          setState(prev_5 => ({
            ...prev_5,
            isLoading: false,
            isComplete: true,
            logs: []
          }));
          return;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      if (!reader) {
        throw new Error("Response body is not readable");
      }
      try {
        let accumulatedLogs: JobLogsResponse = [];
        let batchCounter = 0;
        let buffer = ""; // Buffer for incomplete JSON

        while (true) {
          const {
            done,
            value
          } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, {
            stream: true
          });
          buffer += chunk;

          // Split by newlines but keep incomplete lines in buffer
          const lines = buffer.split("\n");
          buffer = lines.pop() || ""; // Keep the last (potentially incomplete) line in buffer

          for (const line of lines) {
            const trimmedLine = line.trim();
            if (!trimmedLine) continue;
            if (controller_0.signal.aborted) {
              return;
            }
            try {
              const parsed = JSON.parse(trimmedLine);
              if (parsed.error) {
                throw new Error(parsed.error);
              }
              if (parsed.end) {
                // End of stream marker
                setState(prev => ({
                  ...prev,
                  isLoading: false,
                  isComplete: true,
                  totalRecords: parsed.total_records || prev.logs.length
                }));
                return;
              }
              if (parsed.data && Array.isArray(parsed.data)) {
                accumulatedLogs = [...accumulatedLogs, ...parsed.data];
                batchCounter++;

                // Track the highest log_idx so subsequent polls can resume from there.
                for (const row of parsed.data) {
                  if (typeof row?.log_idx === "number" && row.log_idx > lastLogIdxRef.current) {
                    lastLogIdxRef.current = row.log_idx;
                  }
                }

                // Update state with new batch of logs
                setState(prev => ({
                  ...prev,
                  logs: accumulatedLogs,
                  batchCount: batchCounter
                }));
              }
            } catch (parseError) {
              console.warn("Failed to parse streaming log line:", line, parseError);
            }
          }
        }

        // If we exit the loop without seeing an end marker, mark as complete
        setState(prev => ({
          ...prev,
          isLoading: false,
          isComplete: true
        }));
      } finally {
        reader.releaseLock();
      }
    } catch (error_0: any) {
      if (controller_0.signal.aborted) {
        // Request was cancelled, don't update state
        return;
      }
      setState(prev_4 => ({
        ...prev_4,
        isLoading: false,
        isComplete: false,
        error: error_0 instanceof Error ? error_0 : new Error(String(error_0))
      }));
    }
  }, [streamingFetch, activeGithubOrg, jobId, enabled, runnerName]);

  // Append-only delta fetch used while a job is in progress. Asks the server
  // for rows past the last log_idx we already have and appends them. Skipped
  // entirely while the initial streaming fetch is still running, while a blob
  // download is underway, or when the user has selected a container log source
  // (R2-only). Errors are swallowed so a transient blip doesn't tear down the
  // already-rendered logs.
  const fetchStreamingDelta = useCallback(async () => {
    if (!activeGithubOrg || !jobId || !enabled) {
      return;
    }
    if (selectedLogSource != null) {
      return;
    }
    if (pollAbortControllerRef.current) {
      pollAbortControllerRef.current.abort();
    }
    const controller_1 = new AbortController();
    pollAbortControllerRef.current = controller_1;
    try {
      const offset = lastLogIdxRef.current;
      let url_0 = `user/github/orgs/${activeGithubOrg}/metrics/logs/job/stream?job_id=${jobId}&limit=10000&offset=${offset}`;
      if (runnerName) {
        url_0 += `&vm_id=${encodeURIComponent(runnerName)}`;
      }
      const response_0 = await streamingFetch(url_0, {
        signal: controller_1.signal
      });
      if (!response_0.ok) {
        return;
      }
      const reader_0 = response_0.body?.getReader();
      const decoder_0 = new TextDecoder();
      if (!reader_0) return;
      try {
        let buffer = "";
        const newRows: JobLogsResponse = [];

        // Advance the polling cursor only after rows are actually committed to
        // state. If we bumped lastLogIdxRef inside the parse loop and the fetch
        // was then aborted (poll cleanup, isJobRunning flipping, or a new tick
        // pre-empting us), the ref would skip past rows that never made it into
        // logs and the next poll would never request them again.
        const commitNewRows = () => {
          if (newRows.length === 0) return;
          // Snapshot the buffered rows. The setState updater is deferred (React
          // batches it) and runs against the array reference at flush time, so
          // mutating newRows after calling setState would race the updater and
          // produce an empty append. Take a copy now and clear the buffer
          // immediately so subsequent batches start fresh.
          const rowsToCommit = newRows.slice();
          newRows.length = 0;
          let maxLogIdx = lastLogIdxRef.current;
          for (const row of rowsToCommit) {
            if (typeof row?.log_idx === "number" && row.log_idx > maxLogIdx) {
              maxLogIdx = row.log_idx;
            }
          }
          setState(prev => ({
            ...prev,
            logs: [...prev.logs, ...rowsToCommit],
            batchCount: prev.batchCount + 1
          }));
          lastLogIdxRef.current = maxLogIdx;
        };
        while (true) {
          const {
            done,
            value
          } = await reader_0.read();
          if (done) break;
          if (controller_1.signal.aborted) return;
          const chunk = decoder_0.decode(value, {
            stream: true
          });
          buffer += chunk;
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";
          for (const line of lines) {
            const trimmedLine = line.trim();
            if (!trimmedLine) continue;
            try {
              const parsed = JSON.parse(trimmedLine);
              if (parsed.error) {
                return;
              }
              if (parsed.end) {
                commitNewRows();
                return;
              }
              if (parsed.data && Array.isArray(parsed.data)) {
                for (const row of parsed.data) {
                  newRows.push(row);
                }
              }
            } catch {
              // Ignore parse errors during polling; the next tick will retry.
            }
          }
        }
        commitNewRows();
      } finally {
        reader_0.releaseLock();
      }
    } catch {
      // Network error during polling: leave existing logs alone and try again
      // on the next tick. The user-facing error path is owned by the initial
      // fetch in fetchStreamingLogs.
    }
  }, [streamingFetch, activeGithubOrg, jobId, enabled, runnerName, selectedLogSource]);

  // Main fetch logic that tries blob storage first, then falls back to streaming
  const fetchLogs = useCallback(async () => {
    if (!enabled || !activeGithubOrg || !jobId) {
      setState({
        logs: [],
        isLoading: false,
        isComplete: false,
        error: null,
        totalRecords: null,
        batchCount: 0
      });
      return;
    }

    // Set loading state immediately when enabled to prevent flicker
    // This ensures we show loading state even while waiting for metadata
    // Note: We only check !prev.isLoading (not isComplete) because when switching
    // between jobs, the previous job's isComplete=true would prevent showing loading
    setState(prev_6 => {
      if (!prev_6.isLoading) {
        return {
          ...prev_6,
          isLoading: true,
          isComplete: false
        };
      }
      return prev_6;
    });

    // Wait for metadata check to complete
    if (isMetadataLoading) {
      return;
    }

    // Prevent duplicate fetches
    if (hasFetchedRef.current) {
      return;
    }
    hasFetchedRef.current = true;
    const effectiveSource = selectedLogSource ?? null;
    const entry_0 = effectiveSource?.sourceType != null && effectiveSource?.sourceName != null ? getLogsMetadataEntryBySource(metadataRef.current ?? null, effectiveSource.sourceType, effectiveSource.sourceName) : getDefaultLogsMetadataEntry(metadataRef.current ?? null);

    // Try blob storage first if we have an entry for the selected (or default) source
    if (entry_0) {
      const result = await fetchFromBlobStorage(effectiveSource);
      if (result === "aborted") {
        return; // Request was cancelled, don't start streaming
      }
      if (result === true) {
        return; // Successfully loaded from blob storage
      }
    }

    // Fall back to streaming from ClickHouse (main-job only; container logs are R2-only)
    fetchStreamingLogs();
  }, [enabled, activeGithubOrg, jobId, isMetadataLoading, selectedLogSource, fetchFromBlobStorage, fetchStreamingLogs]);

  // Reset and refetch when dependencies change
  useEffect(() => {
    // Reset fetch tracking when key params change
    hasFetchedRef.current = false;
    // Reset polling cursor so the next poll starts from the beginning of the new
    // stream rather than reusing the previous job's last_log_idx.
    lastLogIdxRef.current = 0;
    fetchLogs();

    // Cleanup on unmount or dependency change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (blobAbortControllerRef.current) {
        blobAbortControllerRef.current.abort();
      }
      if (pollAbortControllerRef.current) {
        pollAbortControllerRef.current.abort();
      }
    };
  }, [enabled, activeGithubOrg, jobId, runnerName, isMetadataLoading, selectedLogSource, fetchLogs]);

  // Poll for new log lines while the job is still running. We only start the
  // interval after the initial fetch has signaled completion (state.isComplete)
  // so we don't race the streaming reader. When isJobRunning flips to false, we
  // stop polling and let the next render reach a stable end state.
  useEffect(() => {
    if (!isJobRunning) return;
    if (!enabled || !activeGithubOrg || !jobId) return;
    if (selectedLogSource != null) return;
    if (!state.isComplete) return;
    const intervalId = setInterval(() => {
      fetchStreamingDelta();
    }, RUNNING_JOB_LOG_POLL_INTERVAL_MS);
    return () => {
      clearInterval(intervalId);
      if (pollAbortControllerRef.current) {
        pollAbortControllerRef.current.abort();
      }
    };
  }, [isJobRunning, enabled, activeGithubOrg, jobId, selectedLogSource, state.isComplete, fetchStreamingDelta]);
  const refetch = useCallback(() => {
    hasFetchedRef.current = false;
    lastLogIdxRef.current = 0;
    fetchLogs();
  }, [fetchLogs]);
  return {
    ...state,
    refetch
  };
}